/**
 * Identity class - manages user identity and signing
 */
import type { KeyPair } from './keys.js';
import { generateKeyPair, importKeyPair, exportKeyPair } from './keys.js';
import { signObject, verifySignature } from './signing.js';
import { createDAGObject, computeHash } from '../dag/object.js';
import type { DAGObject, ContentTypes, ObjectType, DAGObjectWithoutHash } from '../dag/types.js';

export class Identity {
  private keyPair: KeyPair;

  constructor(keyPair: KeyPair) {
    this.keyPair = keyPair;
  }

  /**
   * Generate a new identity
   */
  static generate(): Identity {
    return new Identity(generateKeyPair());
  }

  /**
   * Import identity from stored JSON
   */
  static fromJSON(json: string): Identity {
    return new Identity(importKeyPair(json));
  }

  /**
   * Export identity to JSON for storage
   */
  toJSON(): string {
    return exportKeyPair(this.keyPair);
  }

  /**
   * Get public key as base58 string
   */
  get publicKey(): string {
    return this.keyPair.publicKeyBase58;
  }

  /**
   * Get the underlying key pair (for signing operations)
   */
  getKeyPair(): KeyPair {
    return this.keyPair;
  }

  /**
   * Get shortened public key for display
   */
  get shortId(): string {
    const pk = this.publicKey;
    return `${pk.slice(0, 6)}...${pk.slice(-4)}`;
  }

  /**
   * Create and sign a DAG object
   */
  createObject<T extends ContentTypes>(
    type: ObjectType,
    content: T,
    parents: string[] = []
  ): DAGObject<T> {
    const objWithoutHash: DAGObjectWithoutHash<T> = {
      type,
      creator: this.keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents,
      content,
    };

    const objWithHash = createDAGObject(objWithoutHash);
    return signObject(objWithHash, this.keyPair);
  }

  /**
   * Sign an existing object (that has hash but no signature)
   */
  sign<T extends ContentTypes>(obj: Omit<DAGObject<T>, 'signature'>): DAGObject<T> {
    return signObject(obj, this.keyPair);
  }

  /**
   * Verify a signature on an object
   */
  static verify(obj: DAGObject): boolean {
    return verifySignature(obj);
  }
}
